/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable } from '@angular/core'
import { DynamicFormControlModel } from '../model/dynamic-form-control.model'
import {
  DynamicFormControlLayout,
  DynamicFormControlLayoutConfig,
  DynamicFormControlLayoutContext,
  DynamicFormControlLayoutPlace,
} from '../model/misc/dynamic-form-control-layout.model'
import { isObject, isString } from '../utils/core.utils'

export type DynamicFormLayout = { [id: string]: DynamicFormControlLayout }

@Injectable({
  providedIn: 'root',
})
export class DynamicFormLayoutService {
  findById(id: string, formLayout: DynamicFormLayout | null): DynamicFormControlLayout | null {
    if (isObject(formLayout)) {
      for (const key of Object.keys(formLayout)) {
        if (key === id) {
          return formLayout[key]
        }
      }
    }

    return null
  }

  findByModel(model: DynamicFormControlModel, formLayout: DynamicFormLayout | null): DynamicFormControlLayout | null {
    let controlLayout: DynamicFormControlLayout | null = null

    if (isObject(formLayout)) {
      for (const key of Object.keys(formLayout)) {
        key.split(',').forEach((substring) => {
          const selector = substring.trim()

          if (selector === model.id || selector === model.type) {
            controlLayout = formLayout[key]
          }
        })
      }
    }

    return controlLayout
  }

  getClass(
    layout: DynamicFormControlLayout | null | undefined,
    context: DynamicFormControlLayoutContext,
    place: DynamicFormControlLayoutPlace,
  ): string {
    if (isObject(layout) && layout.hasOwnProperty(context)) {
      const config = layout[context] as DynamicFormControlLayoutConfig

      if (config.hasOwnProperty(place)) {
        return this.prefixTailwindClasses(config[place] as string)
      }
    }

    return ''
  }

  getHostClass(layout: DynamicFormControlLayout | null | undefined): string {
    const keys: (keyof DynamicFormControlLayout)[] = ['element', 'grid']
    let cls = ''

    if (isObject(layout)) {
      keys.forEach((key) => {
        if (isObject(layout[key]) && isString(layout[key].host)) {
          cls = cls + ` ${this.prefixTailwindClasses(layout[key].host)}`
        }
      })
    }

    return cls
  }

  /**
   * Tailwind runs in prefixed mode (`profile:`). Form layout class lists are data-driven
   * (delivered by the API at runtime), so they cannot be picked up by Tailwind's static
   * source scanning and must be namespaced with the `profile:` prefix at runtime to match
   * the generated utilities. Already-prefixed tokens are left untouched (idempotent).
   */
  private prefixTailwindClasses(value: string | null | undefined): string {
    if (!isString(value) || value.trim() === '') {
      return value ?? ''
    }

    return value.replace(/\S+/g, (token) => (token.startsWith('profile:') ? token : `profile:${token}`))
  }

  getElementId(model: DynamicFormControlModel): string {
    return model.id
  }
}
